import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCheck, MailOpen } from "lucide-react";
import { EmptyState } from "../PageChrome";
import { LoadingRows } from "../Skeleton";
import { useAnchoredPlacement } from "../header/useAnchoredPlacement";
import { hasAnyRole, OperationsPrincipals } from "../../auth/roles";
import { useShownRoles } from "../../auth/useShownRoles";
import { badgeText, notificationCentreApi, NOTIFY_QK } from "../../api/notificationCentreApi";
import { useApiQuery } from "../../hooks/useApiQuery";
import { useLiveConnectionState } from "../../hooks/useLiveUpdates";
import { useRelease150 } from "../../lib/release150";
import { NoticeGroups } from "./NoticeGroups";
import { offerMarkAllReadUndo, unreadIds } from "./noticeUndo";

// Release 1.5.0 header bell (O-R55). The badge counts unread notices that are not snoozed. The panel shows the newest
// groups; every action opens a page (a person signs there, never from the bell). Live updates refetch the count when any
// data area moves; while the push connection is not live the count is also checked every minute.

export const BELL_POLL_MS = 60_000;

/** The bell shows only with release 1.5.0 on and an Operations role. */
export function useNotificationBellShown(): boolean {
  const release150 = useRelease150();
  const roles = useShownRoles();
  return release150 && hasAnyRole(roles, OperationsPrincipals);
}

export function NotificationBell() {
  const shown = useNotificationBellShown();
  if (!shown) return null;
  return <Bell150 />;
}

function Bell150() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const live = useLiveConnectionState().status === "live";
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const place = useAnchoredPlacement(buttonRef, open, 416);

  const countQuery = useApiQuery([...NOTIFY_QK.count], notificationCentreApi.count, {
    refetchInterval: live ? false : BELL_POLL_MS,
    refetchOnWindowFocus: true
  });
  const listQuery = useApiQuery([...NOTIFY_QK.list(false)], () => notificationCentreApi.get(false), { enabled: open });

  const unread = countQuery.data?.unreadCount ?? 0;
  const badge = badgeText(unread);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: [...NOTIFY_QK.root] });

  const markAll = async () => {
    const ids = unreadIds(listQuery.data?.groups);
    const res = await notificationCentreApi.markAllRead().catch(() => null);
    refresh();
    if (res) offerMarkAllReadUndo(ids, res.changed, refresh);
  };

  const label = unread === 0 ? "Notifications, none unread" : `Notifications, ${unread} unread`;

  return (
    <div ref={rootRef} className="relative flex-none" data-tour="notifications">
      <button
        ref={buttonRef}
        type="button"
        className="relative inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        aria-label={label}
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="dialog"
        title={label}
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-bell"
      >
        <Bell aria-hidden="true" className="h-4 w-4" />
        {badge && (
          <span
            className="absolute -right-1.5 -top-1.5 min-w-[1.1rem] rounded-full border border-white bg-red-700 px-1 text-center text-[10px] font-semibold leading-4 text-white"
            data-testid="notification-badge"
          >
            {badge}
          </span>
        )}
      </button>
      {open &&
        createPortal(
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label="Your notifications"
          style={place}
          className="fixed z-50 flex max-h-[75vh] flex-col rounded-lg border border-slate-200 bg-white shadow-xl"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
            <p className="text-sm font-semibold text-slate-900">
              Notifications <span className="font-normal text-slate-500">· {unread} unread</span>
            </p>
            {unread > 0 && (
              <button type="button" className="btn-secondary inline-flex items-center gap-1 !px-2 !py-0.5 text-xs" onClick={() => void markAll()} data-testid="bell-mark-all-read">
                <CheckCheck aria-hidden="true" className="h-3.5 w-3.5" />
                Mark all read
              </button>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
            {listQuery.isPending ? (
              <LoadingRows rows={4} label="Loading your notifications…" className="py-2" rowClassName="h-10" />
            ) : listQuery.isError ? (
              <p role="alert" className="py-6 text-center text-xs text-red-700">
                Could not load your notifications. Try again in a moment.
              </p>
            ) : listQuery.data.groups.length === 0 ? (
              <EmptyState title="Nothing for you right now." icon={MailOpen} compact testId="bell-empty">
                When a job you own moves or something waits for your signature, it appears here.
              </EmptyState>
            ) : (
              <NoticeGroups
                groups={listQuery.data.groups.slice(0, 6)}
                compact
                maxPerGroup={3}
                onChanged={refresh}
                onNavigate={() => setOpen(false)}
              />
            )}
          </div>
          <div className="border-t border-slate-200 px-3 py-2 text-right">
            <Link to="/my/notifications" className="table-link text-xs font-medium" onClick={() => setOpen(false)} data-testid="bell-open-all">
              Open all notifications and preferences
            </Link>
          </div>
        </div>,
          document.body
        )}
    </div>
  );
}
